import { useLazyQuery } from '@apollo/client';
import { Autocomplete, Button } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './EvLocationSearch.module.css';

const cx = classnames.bind(styles);

const LOCATION_SEARCH_QUERY = gql`
  query EvLocationSearch($search: String!) {
    locationsBySearchTerm(search: $search) {
      displayName
      latitude
      longitude
    }
  }
`;

/**
 * EvLocationSearch component - Location search input with geocoding for EV stations
 *
 * Provides an autocomplete search box for location search and a "Use Current Location" button.
 * When a location is selected, it calls onLocationSelect with the coordinates.
 */
export default function EvLocationSearch({
  onLocationSelect,
  onClearLocation,
  hasLocation,
  isLoading,
  placeholder,
  className,
  initialValue,
}) {
  // Clear Location feature disabled pending bug fixes (spec 005)
  // Props preserved for future re-implementation
  const clearLocationEnabled = false;
  const showClearButton = clearLocationEnabled && hasLocation && onClearLocation;
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Update input value when initialValue changes (e.g., from parent's auto-geolocation)
  useEffect(() => {
    if (initialValue && initialValue !== inputValue) {
      setInputValue(initialValue);
    }
  }, [initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchLocations, { data: locationData, loading: isSearching }] = useLazyQuery(
    LOCATION_SEARCH_QUERY,
    { fetchPolicy: 'cache-first' },
  );

  // Debounced search effect
  useEffect(() => {
    if (inputValue.length < 3) {
      return undefined;
    }

    const debounceTimer = setTimeout(() => {
      searchLocations({ variables: { search: inputValue } });
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [inputValue, searchLocations]);

  const handleInputChange = useCallback((value) => {
    setInputValue(value);
    setLocationError(null);
  }, []);

  const handleSelectLocation = useCallback(({ value: location }) => {
    if (location && location.latitude && location.longitude) {
      setInputValue(location.displayName);
      onLocationSelect({
        latitude: location.latitude,
        longitude: location.longitude,
        displayName: location.displayName,
      });
    }
  }, [onLocationSelect]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setInputValue(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        onLocationSelect({
          latitude,
          longitude,
          displayName: 'Current Location',
        });
        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission was denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information is unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('Unable to get your location. Please enter an address.');
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      },
    );
  }, [onLocationSelect]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      // Check if input looks like coordinates (lat, lng format)
      const coordMatch = inputValue.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
      if (coordMatch) {
        const latitude = parseFloat(coordMatch[1]);
        const longitude = parseFloat(coordMatch[2]);
        if (latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180) {
          onLocationSelect({
            latitude,
            longitude,
            displayName: `${latitude}, ${longitude}`,
          });
        }
      }
    }
  }, [inputValue, onLocationSelect]);

  // Transform location data into autocomplete options
  const locationOptions = useMemo(() => {
    if (!locationData?.locationsBySearchTerm) {
      return [];
    }

    return locationData.locationsBySearchTerm
      .filter(loc => loc.latitude && loc.longitude)
      .map(loc => ({
        label: loc.displayName,
        value: loc,
      }));
  }, [locationData]);

  return (
    <div className={cx('locationSearch', className)}>
      <div className={cx('inputWrapper')}>
        <Autocomplete
          autoComplete="off"
          className={cx('searchInput')}
          loading={isSearching || isLoading}
          name="evLocationSearch"
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onSelect={handleSelectLocation}
          options={locationOptions}
          placeholder={placeholder}
          value={inputValue}
        />
      </div>

      <Button
        className={cx('locationButton')}
        disabled={isGettingLocation || isLoading}
        onClick={handleUseCurrentLocation}
        primary
      >
        {isGettingLocation ? 'Getting Location...' : 'Use Current Location'}
      </Button>

      {/* Clear Location button disabled pending bug fixes - see spec 005
          To re-enable: set clearLocationEnabled = true at top of component */}
      {showClearButton && (
        <Button
          className={cx('clearButton')}
          disabled={isLoading}
          onClick={onClearLocation}
          variant="secondary"
        >
          ✕ Clear Location
        </Button>
      )}

      {locationError && (
        <p className={cx('error')}>{locationError}</p>
      )}
    </div>
  );
}

EvLocationSearch.propTypes = {
  onLocationSelect: PropTypes.func.isRequired,
  onClearLocation: PropTypes.func,
  hasLocation: PropTypes.bool,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  initialValue: PropTypes.string,
};

EvLocationSearch.defaultProps = {
  onClearLocation: undefined,
  hasLocation: false,
  isLoading: false,
  placeholder: 'Enter city or ZIP code',
  className: undefined,
  initialValue: '',
};
